import React from 'react'
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole
} from '@floating-ui/react'
import BottomSheet from '@/component/modal/BottomSheet.tsx'
import useWindowSize from '@/util/hook/useWindowSize.ts'

const GAP = 8
const VIEWPORT_PADDING = 16

interface ITriggerProps {
    ref: (node: HTMLElement | null) => void
    props: Record<string, unknown>
}

interface IProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    placement: Placement
    sheetMaxWidth: number
    ariaLabel: string
    surfaceClassName?: string
    renderTrigger: (trigger: ITriggerProps) => React.ReactNode
    children: React.ReactNode
}

const ResponsivePopover: React.FC<IProps> = ({
    isOpen,
    onOpenChange,
    placement,
    sheetMaxWidth,
    ariaLabel,
    surfaceClassName,
    renderTrigger,
    children
}) => {
    const { windowSize } = useWindowSize()
    const isSheet = windowSize.width !== undefined && windowSize.width <= sheetMaxWidth

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange,
        placement,
        middleware: [offset(GAP), flip({ padding: VIEWPORT_PADDING }), shift({ padding: VIEWPORT_PADDING })],
        whileElementsMounted: autoUpdate
    })
    const click = useClick(context)
    const dismiss = useDismiss(context, { enabled: !isSheet })
    const role = useRole(context, { role: 'dialog' })
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

    return (
        <>
            {renderTrigger({ ref: refs.setReference, props: getReferenceProps() })}
            {isOpen && !isSheet && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        aria-label={ariaLabel}
                        className={`test-mode-popover ${surfaceClassName || ''}`}
                        {...getFloatingProps()}>
                        {children}
                    </div>
                </FloatingPortal>
            )}
            {isSheet && (
                <BottomSheet open={isOpen} onClose={() => onOpenChange(false)} ariaLabel={ariaLabel}>
                    {children}
                </BottomSheet>
            )}
        </>
    )
}

export default ResponsivePopover
